/**
 * The settings map: one table, used by both the shell nav and the hub.
 *
 * `settings.md` A4 item 3: the nine cards were maintained by hand in two
 * files (`settings/index.tsx` and `settings/_layout/index.tsx`) and had
 * already diverged: the layout grouped them into You / Organisation /
 * Developer and carried an Overview entry, while the hub was a flat nine in a
 * different order.
 *
 * Labels are catalog keys, not copy: this is module scope, so it cannot call
 * the hook; the render resolves each key through `t`.
 */
export interface SettingsLink {
  labelKey: string;
  /** One line saying what the page is for, shown on the hub cards. */
  descriptionKey: string;
  path: string;
  /** For the organisation's owner and admins only (roles, 2 October 2026). */
  adminOnly?: boolean;
  end?: boolean;
  /** Overview is nav-only; it is the hub itself. */
  hubHidden?: boolean;
  /** Shown only when the platform offers this (`sms`: SMS has a provider; `email`: email has one). */
  feature?: SettingsFeature;
}

/** The channels a settings page depends on: the page is hidden while the platform cannot send by it. */
export type SettingsFeature = 'sms' | 'email';

export interface SettingsSection {
  labelKey: string;
  items: SettingsLink[];
}

export const SETTINGS_NAV: SettingsSection[] = [
  {
    labelKey: 'common.you',
    items: [
      {
        labelKey: 'common.overview',
        descriptionKey: 'settings.hub.overview.hint',
        path: '/settings',
        end: true,
        hubHidden: true
      },
      {
        // A4 item 8: this borrowed `contacts.view.tabs.profile`, so renaming a
        // contacts tab would have renamed a settings page.
        labelKey: 'settings.profile.title',
        descriptionKey: 'settings.hub.profile.hint',
        path: '/settings/profile'
      },
      {
        labelKey: 'settings.nav.security',
        descriptionKey: 'settings.hub.security.hint',
        path: '/settings/security'
      },
      {
        labelKey: 'settings.nav.notifications',
        descriptionKey: 'settings.hub.notifications.hint',
        path: '/settings/notifications'
      },
      {
        labelKey: 'settings.nav.appearance',
        descriptionKey: 'settings.hub.appearance.hint',
        path: '/settings/appearance'
      }
    ]
  },
  {
    labelKey: 'settings.nav.organisationGroup',
    items: [
      {
        // A4 item 11: the group heading and this item used the same key, so it
        // read "Organisation › Organisation".
        labelKey: 'settings.nav.organisation',
        descriptionKey: 'settings.hub.organisation.hint',
        path: '/settings/organisation',
        adminOnly: true
      },
      {
        labelKey: 'settings.assistant.title',
        descriptionKey: 'settings.hub.assistant.hint',
        path: '/settings/assistant',
        adminOnly: true
      },
      {
        labelKey: 'settings.nav.team',
        descriptionKey: 'settings.hub.team.hint',
        path: '/settings/team'
      },
      {
        labelKey: 'canned.title',
        descriptionKey: 'settings.hub.canned.hint',
        path: '/settings/canned-replies'
      },
      {
        // The extra details kept about each contact: named, typed and switched
        // on or off here; read by the contact form, filters, segments, import
        // and export.
        labelKey: 'settings.nav.contactFields',
        descriptionKey: 'settings.hub.contactFields.hint',
        path: '/settings/contact-fields',
        adminOnly: true
      },
      {
        // SMS (4 October 2026): the WhatsApp-to-SMS fallback switch. Hidden
        // everywhere until the platform has an SMS provider.
        labelKey: 'settings.nav.sms',
        descriptionKey: 'settings.hub.sms.hint',
        path: '/settings/sms',
        adminOnly: true,
        feature: 'sms'
      },
      {
        // Email (4 October 2026): the business's address, sender, the
        // WhatsApp-to-email fallback and its own domain. Everybody can read
        // it; only owners and admins can change it, so it is not adminOnly.
        // Hidden everywhere until the platform has an email provider.
        labelKey: 'settings.nav.email',
        descriptionKey: 'settings.hub.email.hint',
        path: '/settings/email',
        feature: 'email'
      },
      {
        // A4 item 4: money-and-account-shaped, absent from the hub, so a
        // tenant looking for "how do I get an invoice" found nothing here.
        labelKey: 'nav.billing',
        descriptionKey: 'settings.hub.billing.hint',
        path: '/billing',
        adminOnly: true
      }
    ]
  },
  {
    labelKey: 'settings.nav.developer',
    items: [
      {
        labelKey: 'settings.nav.apiKeys',
        descriptionKey: 'settings.hub.apiKeys.hint',
        path: '/settings/api-keys',
        adminOnly: true
      },
      {
        labelKey: 'settings.nav.webhooks',
        descriptionKey: 'settings.hub.webhooks.hint',
        path: '/settings/webhooks',
        adminOnly: true
      }
    ]
  }
];

/** Whether a settings link shows: owner-and-admin pages for those roles, channel pages while the channel exists. */
export function settingsLinkShown(
  item: Pick<SettingsLink, 'adminOnly' | 'feature'>,
  isAdmin: boolean,
  features: Record<SettingsFeature, boolean>
): boolean {
  return (isAdmin || !item.adminOnly) && (item.feature === undefined || features[item.feature]);
}

/** The nav entry whose path matches, longest match first. */
export function activeSettingsLink(pathname: string): SettingsLink | null {
  const items = SETTINGS_NAV.flatMap((s) => s.items);
  return items
    .filter((i) => (i.end ? pathname === i.path : pathname.startsWith(i.path)))
    .sort((a, b) => b.path.length - a.path.length)[0] ?? null;
}
